import { ApplicationSelectData, ApplicationType } from '@sec-loans-ui/common/types'
import { RootStepType } from '@sec-loans-ui/constants'
import { useSendSecLoansPageViewEvent } from '@sec-loans-ui/hooks/useSendSecLoansPageViewEvent'
import { useAnalyticsService } from '@sec-loans-ui/state/analyticsContext'
import { User } from '@sec-loans-ui/wizard/types'
import { getFullname } from '@ui-common/utils'
import { PlusIcon } from '@ui-components/assets/Icons'
import { FormSection } from '@ui-components/global/FormStyling'
import { CatalogStep } from '@ui-components/layout/CatalogStep'
import { useIntl } from 'react-intl'

import { ExistingApplicationsView } from './components/ExistingApplicationsView'
import { NoExistingApplicationsView } from './components/NoExistingApplicationsView'
import { getApplicationDetails } from './loanCatalogStepUtils'

export interface LoanCatalogStepProps {
  onApplicationSelect: (input: ApplicationSelectData) => void
  onNewApplicationSelect: () => void
  onAbort: () => unknown
  user: User
}

export const LoanCatalogStep = ({
  onApplicationSelect,
  onNewApplicationSelect,
  onAbort,
  user,
}: LoanCatalogStepProps) => {
  useSendSecLoansPageViewEvent(RootStepType.Catalog)
  const { analyticsService } = useAnalyticsService()
  const { formatMessage } = useIntl()

  const { inProgressApplications, completedApplications } = getApplicationDetails(
    user.loanApplications,
    user.ifpApplications,
  )

  const hasExistingApplications = inProgressApplications.length > 0 || completedApplications.length > 0

  const handleApplicationSelect = (input: ApplicationSelectData) => {
    if (input.applicationType === ApplicationType.LoanApplication) {
      analyticsService.sendStepCustomEvent(RootStepType.Catalog, `SelectLoanApplication`)
    } else if (input.applicationType === ApplicationType.InstalmentFreePeriodApplication) {
      analyticsService.sendStepCustomEvent(RootStepType.Catalog, 'SelectIfpApplication')
    }
    onApplicationSelect(input)
  }

  return (
    <CatalogStep
      actionBarOptions={{
        label: formatMessage({ id: 'application-list-new-application' }),
        icon: <PlusIcon />,
        onClick: onNewApplicationSelect,
      }}
      actionButtonOptions={{
        label: formatMessage({ id: 'common-exit' }),
        onClick: onAbort,
      }}
      displayName={getFullname({ firstName: user.firstNames, lastName: user.lastName })}
      header={formatMessage({ id: 'application-list-header' })}
    >
      <FormSection>
        {hasExistingApplications ? (
          <ExistingApplicationsView
            userSsn={user.ssn}
            inProgressApplications={inProgressApplications}
            completedApplications={completedApplications}
            onApplicationSelect={handleApplicationSelect}
          />
        ) : (
          <NoExistingApplicationsView onNewApplicationSelect={onNewApplicationSelect} />
        )}
      </FormSection>
    </CatalogStep>
  )
}
